import { ArrowRight, BookOpen, CheckCircle2, Download } from "lucide-react";
import Navbar from "@/components/Navbar";
import LeadMagnet from "@/components/LeadMagnet";
import Testimonials from "@/components/Testimonials";
import FAQ from "@/components/FAQ";
import Footer from "@/components/Footer";
import { usePageSEO } from "@/hooks/usePageSEO";

const PAGE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Book",
  "name": "The Passion Centered Person",
  "alternateName": "How to Go From Having Just a Career to Having a Life",
  "author": [
    { "@type": "Person", "name": "Gary Zelesky" },
    { "@type": "Person", "name": "Tim Vandehey" }
  ],
  "description": "Gary Zelesky's signature work, co-authored with Tim Vandehey and endorsed by Mark Victor Hansen (Chicken Soup for the Soul). The book on rediscovering passion — the internal fuel that helps people rise, adjust, reconnect, and keep moving forward. Broad-based appeal beyond any single profession.",
  "url": "https://garyzelesky.com/the-passion-centered-person",
  "inLanguage": "en",
  "isbn": "0983407932",
  "sameAs": "https://www.amazon.com/dp/0983407932",
  "review": {
    "@type": "Review",
    "author": { "@type": "Person", "name": "Mark Victor Hansen" },
    "reviewBody": "You are a WOW of a WOW!",
    "publisher": "Co-author of Chicken Soup for the Soul"
  }
};

export default function PassionBookPage() {
  usePageSEO({
    title: "The Passion Centered Person | Book by Gary Zelesky",
    description: "The Passion Centered Person by Gary Zelesky with Tim Vandehey — how to go from having just a career to having a life. Endorsed by Mark Victor Hansen (Chicken Soup for the Soul). Get Chapter 1 free.",
    canonical: "https://garyzelesky.com/the-passion-centered-person",
    schema: PAGE_SCHEMA,
    schemaId: "book-page-schema",
  });

  const bullets = [
    "The Passion Cycle™: Passion → Productivity → Profitability → Pleasure",
    "How to activate personal and team passion drivers",
    "Build a 'referral mindset' that strengthens relationships and grows business",
    "Practical tools to increase both energy and results — immediately",
  ];

  return (
    <div data-testid="book-page" className="bg-[#050505] text-white">
      <Navbar />

      <section className="relative pt-40 pb-24 px-6 md:px-10 overflow-hidden grain">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-[2px] w-12 bg-[#EC1985]" />
              <span className="eyebrow">Signature Book · Gary Zelesky × Tim Vandehey</span>
            </div>
            <h1 className="h-display text-5xl md:text-7xl lg:text-8xl pb-2">
              The Passion<br />
              <span className="text-[#EC1985]">Centered</span><br />
              Person.
            </h1>
            <div className="mt-4 text-lg md:text-xl text-white/80 font-bold uppercase tracking-[0.15em]">
              How to Go From Having Just a Career to Having a Life
            </div>

            {/* Mark Victor Hansen endorsement */}
            <div className="mt-8 border-l-4 border-[#EC1985] pl-5 py-1 max-w-xl">
              <p className="font-display text-2xl md:text-3xl text-white leading-tight">
                "You are a WOW of a WOW!"
              </p>
              <div className="mt-2 text-sm uppercase tracking-[0.15em] text-white/70 font-bold">
                — Mark Victor Hansen · Co-author of Chicken Soup for the Soul
              </div>
            </div>

            <p className="mt-8 text-white/80 text-lg leading-relaxed max-w-2xl">
              Passion is not personality. It's not motivation. It's not hype. It is the
              internal fuel that helps people rise, adjust, reconnect, and keep moving
              forward. Co-authored with Tim Vandehey, built from 40+ years on the
              platform and lived through a heart attack Gary survived.
            </p>

            <ul className="mt-8 space-y-3 max-w-xl">
              {bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-white/85">
                  <CheckCircle2 size={18} className="text-[#EC1985] flex-shrink-0 mt-1" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-12 flex flex-col sm:flex-row gap-4">
              <a href="#free-chapter" className="btn-primary" data-testid="book-hero-cta">
                <Download size={18} /> Get Chapter 1 Free
              </a>
              <a href="mailto:Cherisse@GaryZelesky.com?subject=The%20Passion%20Centered%20Person%20-%20Bulk%20Order" className="btn-ghost">
                Bulk Order for Your Team <ArrowRight size={18} />
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative max-w-sm mx-auto">
              <div className="absolute inset-0 bg-[#EC1985] translate-x-6 translate-y-6" />
              <img
                src="/images/book-passion-centered.png"
                alt="The Passion Centered Person book cover"
                className="relative w-full border border-[#262626]"
              />
            </div>
          </div>
        </div>
      </section>

      <LeadMagnet />
      <Testimonials />
      <FAQ />
      <Footer />
    </div>
  );
}
